/*globals globalNavigate*/
import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  forwardRef,
  useRef,
} from "react";
import styled from "styled-components";
import * as d3 from "d3";
import Request from "common/Request";
import ColumnsGrid from "common/layouts/ColumnsGrid";
import { useDimensions } from "common/hooks";
import {
  safeStr,
  doSystemDateRetrieval,
  parseSystemDate,
  calcRangeExtent,
  isInsideRange,
  getBqnIdCmdPart,
  csvName,
} from "common/api";
import BqnContext from "common/BqnContext";
import RangeSliderInput from "common/RangeSliderInput";
import { TileContainer } from "common/graphs/FacetGraph";
import ActionsContext from "common/ActionsContext";
import ScatterPlot from "common/graphs/ScatterPlot/ScatterPlot";
import formats from "common/graphs/formats";
import SelectInputWithIcon from "common/SelectInputWithIcon";
import FacetGraph from "common/graphs/ConnectedFacetGraph";
import {
  parseSubscriberVolume,
  parseSubscriberFlows,
  calcFlowsCreationPerMinute,
  parseSubscriberLatencies,
  parseSubscriberRetransmissions,
  parseSubscriberCongestion,
  parseActiveSubscribers,
  asJSAttribute
} from "./api";
import RatePolicy from "../../Status/SubscriberGroupsEdit/RatePolicy";
import { hoursScopeChoices, periodAsHours } from "common/constants";
import Switch from "@mui/material/Switch";
import { getThresholds, metricThresholdMap } from "views/Status/Subscriber&Groups/utils";
import { metricChoices } from "../SubscriberDetail/SubscriberAnalytics";

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  h4.chart-title {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
  }
`;

const SelectWrapper = styled.div`
  width: 180px;
  margin-bottom: 0px;
`;

const WrapperSwitch = styled.div`
  display: flex;
  align-items: center;
  .sw-input.MuiSwitch-root {
    & .Mui-checked {
      color: var(--switch-checked-green);
    }

    & .Mui-checked + .MuiSwitch-track {
      background-color: var(--switch-checked-light-green);
    }
  }
`;

const WrapperSelectors = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const doesNothing = () => {};
const MARGIN_PLOT = { top: 0, right: 40, bottom: 50, left: 60 };
const PADDING_CHART_CONTAINER = 30;

const expressSubscriberGroup = (subscriberGroup, facet) =>
  facet === "ip-active"
    ? `'${csvName(subscriberGroup)}'`
    : safeStr(subscriberGroup);

export const doStatsRetrieval = ({
  facet = "volume",
  hours = 24,
  interval = 10,
  subscriberGroup,
  mode,
  bqnId,
}) => {
  if (facet === "retransmission") {
    const csvList = `'${csvName(subscriberGroup)}`+ (subscriberGroup === "all-subscribers" ? "'" : `,"all-subscribers"'`);
    return ifCl.run(
      `show statistics subscriber-groups name ${csvList} retransmission-down hours ${hours} interval ${interval}`,
    );
  }

  if (facet.startsWith("latency-")) {
    return ifCl.run(
      `show statistics subscriber-groups name '${csvName(
        subscriberGroup,
      )},"all-subscribers"' ${facet} hours ${hours} interval ${interval}`,
    );
  }
  return ifCl.run(
    `show statistics subscriber-groups name ${expressSubscriberGroup(
      subscriberGroup,
      facet
    )} ${facet} hours ${hours} interval ${interval}${getBqnIdCmdPart(
      mode,
      bqnId
    )}`
  );
};

const ControlsTileContainer = styled.div`
  min-height: 10cm;
  @media print {
    & {
      height: 8cm;
      &.can-compress {
        height: auto;
      }
      width: 100%;
      display: block:
    }
  }
  display: flex;
  flex-flow: row wrap;
  & > h4 {
    text-align: center;
    margin: 20px 0 0 0;
  }
  & > .graph {
    flex: 1 1 100%;
    .nv-y.nv-axis .nv-axisMaxMin {
      display: none;
    }
  }
  &.spread {
    justify-content: space-between;
    padding: 1em;
    align-content: flex-start;
  }
  &.can-compress {
    min-height: 0;
  }
  .input-group {
    margin-bottom: 0px;
  }
  .dont-grow-tall {
    height: auto;
  }
`;
const ReloadButton = styled.div`
  cursor: pointer;

  a {
    display: flex;
    text-decoration: none;
  }

  .material-icons {
    font-size: 20px;
  }
`;

const IconRefresh = styled.i`
  color: #999;
  &:hover {
    color: #000;
  }
  transition: all 0.5s;
`;

const RatePolicyContainer = styled.div`
  padding-top: 2px;

  .rate-policy-title {
    font-size: 12px;
  }

  button {
    font-size: 12px;
  }

  @media (max-width: 720px) {
    width: 100%;
    display: flex;
    justify-content: end;
  }
`;

const StyledButton = styled.button`
  display: flex;
  align-items: center;

  .material-icons {
    padding-right: 10px;
  }
`;

const HoursScopeSelect = ({
  selected = 24,
  onChange = doesNothing,
  ...settings
}) => (
  <SelectInputWithIcon
    title="Date Range"
    name="hours"
    icon="date_range"
    {...settings}
    selected={selected}
    onChange={(event) => onChange({ hours: event.target.value })}
    options={hoursScopeChoices}
  />
);

const returnView = "viewSubscriberGroupDashboard";

function hoursToTimePeriod(hours) {
  switch (hours) {
    case 24:
      return "oneDay";
    case 24 * 7:
      return "oneWeek";
    case 24 * 30:
      return "oneMonth";
    case 24 * 30 * 3:
      return "threeMonths";
  }
}

const ContextControls = ({
  range,
  hours,
  onChange = doesNothing,
  children,
  subscriberGroup,
  settingsSubscriberGroups,
  warningEnabled,
  setWarningEnabled,
}) => {
  const actions = useContext(ActionsContext);

  return (
    <>
      <ControlsTileContainer className="full-width can-compress spread">
      <WrapperSelectors>
          <HoursScopeSelect
            selected={hours}
            onChange={onChange}
            className="dont-grow-tall"
          />
          <WrapperSwitch>
            <div>
              {!warningEnabled
                ? "Colorize warning threshold: disabled"
                : "Colorize warning threshold: enabled"}
            </div>
            <Switch
              className="sw-input"
              checked={warningEnabled}
              onChange={() => setWarningEnabled(!warningEnabled)}
            />
          </WrapperSwitch>
        </WrapperSelectors>
        <div className="row-wrap">
          <RatePolicyContainer>
            <RatePolicy
              nameGroup={subscriberGroup}
              returnView="viewStatusSubscriberGroups"
              settings={settingsSubscriberGroups}
              className="subscriberdashboardPolicyRate"
            />
          </RatePolicyContainer>
          <div className="input-group">
            <StyledButton
              type="button"
              className="btn btn-link waves-effect"
              onClick={() => {
                globalNavigate("viewStatusSubscriberGroups", {
                  subscriberGroup,
                  view: "throughput",
                  ...settingsSubscriberGroups,
                });
              }}
            >
              <span className="input-group-addon" title="Live Throughput">
                <i className="material-icons">insert_chart</i>
              </span>
              Live Throughput
            </StyledButton>
          </div>
          <div className="input-group">
            <StyledButton
              type="button"
              className="btn btn-link waves-effect"
              onClick={() => {
                globalNavigate("viewStatusSubscriberGroups", {
                  returnView,
                  subscriberGroup,
                  ...settingsSubscriberGroups,
                });
              }}
            >
              <span className="input-group-addon" title="Date Range">
                <i className="material-icons">multiline_chart</i>
              </span>
              See main subscribers in group
            </StyledButton>
          </div>
          <div className="input-group">
            <StyledButton
              type="button"
              className="btn btn-link waves-effect"
              onClick={() => {
                globalNavigate("viewStatusSubscriberGroups", {
                  returnView: "viewSubscriberGroupDashboard",
                  subscriberGroup,
                  edit: true,
                  ...settingsSubscriberGroups,
                });
              }}
            >
              <span className="input-group-addon" title="Date Range">
                <i className="material-icons">edit</i>
              </span>
              Edit subscriber group
            </StyledButton>
          </div>
          <div className="input-group">
            <ReloadButton>
              <a
                onClick={() => actions.send("do-load-details")}
                data-toggle="cardloading"
                data-loading-effect="pulse"
                title="Refresh"
              >
                <IconRefresh className="material-icons">refresh</IconRefresh>
              </a>
            </ReloadButton>
          </div>
        </div>
      </ControlsTileContainer>
      {children}
      <TileContainer className="full-width can-compress">
        <RangeSliderInput {...range} onChange={onChange} />
      </TileContainer>
    </>
  );
};

const ScatterPlotLatencySpeed = forwardRef(
  ({ items, interactionData, onInteractionData, metric, setMetric, warning }, ref) => {
    return (
      <TileContainer className="chart-container" ref={ref}>
         <Header>
          <h4 className="chart-title lowercase">Latency vs Average Speed</h4>
          <SelectWrapper>
            <SelectInputWithIcon
              title="Metric"
              name="Metric"
              icon="insert_chart"
              selected={metric}
              onChange={({ target }) => setMetric(target.value)}
              options={metricChoices}
            />
          </SelectWrapper>
        </Header>
        <div className="graph">
          <ScatterPlot
            items={items}
            contentY={{
              yAxisLabel: "Access latency (ms)",
              tooltipLabel: "Latency",
              tooltipUnit: "ms",
            }}
            contentX={{
              xAxisLabel: "Average downlink speed (Mbps)",
              tooltipLabel: "Average Speed",
              tooltipUnit: "Mbps",
            }}
            interactionData={interactionData}
            onInteractionData={onInteractionData}
            key={`latency-speed-scatter-plot-${metric}`}
            warning={warning}
          />
        </div>
      </TileContainer>
    );
  }
);

const parseAndCalcFlowsCreationPerMinute = (response) =>
  calcFlowsCreationPerMinute(parseSubscriberFlows(response));

const formatMillions = (value) =>
  value < 1000
    ? value
    : value < 1000000
    ? `${(value / 1000).toFixed(1)}K`
    : `${(value / 1000000).toFixed(1)}M`;

const scatterItemsXSpeed = (yItems, speedItems, yLabel) => {
  const yLabelNew = yLabel === "subsAccessRtx" ? "subsAccessRtx" : asJSAttribute(yLabel);
  return speedItems.reduce((acc, item, index) => {
    const { downMbps, time } = item;
    if (yItems[index] && yItems[index][yLabelNew] !== null && downMbps !== null)
      acc.push({ y: yItems[index][yLabelNew], x: downMbps, time });
    return acc;
  }, []);
};

const formatDate = d3.timeFormat("%m/%d %H:%M");

function getTimeValuesMaps(itemsCharts) {
  return Object.keys(itemsCharts).reduce((acc, itemsChart) => {
    const mapKey = `${itemsChart}Map`;
    const items = itemsCharts[itemsChart];
    if (items && items.length === 0) {
      acc[mapKey] = {};
    }
    const mapValues = items.reduce((acc, value) => {
      const { time } = value;
      const timeKey = formatDate(time);
      acc[timeKey] = value;
      return acc;
    }, {});
    acc[mapKey] = mapValues;
    return acc;
  }, {});
};

const getSubscriberGroupFields = (subscriberGroup, tooltip) => {
  if(subscriberGroup === "all-subscribers"){
    return  [{
      name: "allSubscribers",
      label: "All Subscribers",
      tooltip,
    }];
  }
  return [
    {
      name: asJSAttribute(subscriberGroup),
      label: subscriberGroup,
      tooltip,
    },
    {
      name: "allSubscribers",
      label: "All Subscribers",
      tooltip,
    },
  ];
};

const getLatencyItems = ({ metric, down, med, eight }) => {
  if (metric === "Rtt-80%-Dn") {
    return eight;
  }
  if (metric === "Rtt-Med-Dn") {
    return med;
  }
  return down;
};

const Dashboard = ({
  target,
  volumes,
  itemsLatencyDown,
  itemsLatencyMed,
  itemsLatency80,
  itemsFlows,
  itemsRetransmission,
  itemsCongestion,
  itemsActiveSubscribers,
  range: initialRange,
  interval = 10,
  hours = 24,
  onHoursChange = doesNothing,
  subscriberGroup,
  settingsSubscriberGroups,
  metric,
  setMetric,
  warningEnabled,
  setWarningEnabled,
}) => {
  if (volumes.length === 0) {
    return null;
  }
  const scatterPlotRef = useRef();
  const [thresholds, setThresholds] = useState({});
  const dimensions = useDimensions(scatterPlotRef);
  const widthChart =
    dimensions.width -
    MARGIN_PLOT.left -
    MARGIN_PLOT.right -
    PADDING_CHART_CONTAINER;
  const [range, setRange] = useState(initialRange);
  const applyRange = (newRange) => setRange(newRange);
  const [interactionData, setInteractionData] = useState(null);
  const applyChange = ({ hours, ...range }) => {
    if (hours !== undefined) {
      onHoursChange(hours);
    } else {
      applyRange(range);
    }
  };

  useEffect(()=> {
    getThresholds().then((response)=> {
      setThresholds(response);
    })
  }, []);

  const itemsLatency = useMemo(() => {
    return getLatencyItems({
      metric,
      down: itemsLatencyDown,
      med: itemsLatencyMed,
      eight: itemsLatency80,
    });
  }, [metric, itemsLatencyDown, itemsLatencyMed, itemsLatency80]);

  const {
    volumesMap,
    itemsLatencyMap,
    itemsFlowsMap,
    itemsRetransmissionMap,
    itemsCongestionMap,
    itemsActiveSubscribersMap,
  } = useMemo(() => {
    return getTimeValuesMaps({
      volumes,
      itemsLatency,
      itemsFlows,
      itemsRetransmission,
      itemsCongestion,
      itemsActiveSubscribers,
    });
  }, [
    volumes,
    itemsLatency,
    itemsFlows,
    itemsRetransmission,
    itemsCongestion,
    itemsActiveSubscribers,
  ]);

  const xScale = useMemo(() => {
    return d3
      .scaleTime()
      .range([0, widthChart])
      .domain([volumes[0].time, volumes[volumes.length - 1].time]);
  }, [volumes, widthChart]);

  return (
    <ColumnsGrid gap="1rem" columns={2} minWidth="9cm" rowGap="0">
      <ContextControls
        target={target}
        className="full-width"
        range={initialRange}
        hours={hours}
        onChange={applyChange}
        subscriberGroup={subscriberGroup}
        settingsSubscriberGroups={settingsSubscriberGroups}
        warningEnabled={warningEnabled}
        setWarningEnabled={setWarningEnabled}
      >
        <FacetGraph
          title="Average Speed Over Time"
          items={volumes.filter(isInsideRange(range))}
          facet="speed"
          yAxisUnits="Mbps"
          fields={[
            { name: "downMbps", label: "Down-Mbps" },
            { name: "upMbps", label: "Up-Mbps" },
          ]}
          yTooltipFormat={formats.decimals(4)}
          onInteractionData={setInteractionData}
          interactionData={interactionData}
          valuesMap={volumesMap}
          xScale={xScale}
          widthChart={widthChart}
          hours={hours}
        />
        <FacetGraph
          title="Flows Over Time"
          items={itemsFlows.filter(isInsideRange(range))}
          yAxisUnits="Flows"
          fields={[
            { name: "flCreatedPerMinute", label: "Fl-Created-per-minute" },
            { name: "flActive", label: "Fl-Active" },
          ]}
          yAxisFormat={formatMillions}
          onInteractionData={setInteractionData}
          interactionData={interactionData}
          valuesMap={itemsFlowsMap}
          xScale={xScale}
          widthChart={widthChart}
          hours={hours}
        />
        <FacetGraph
          title="Latency Over Time"
          items={itemsLatency.filter(isInsideRange(range))}
          yAxisUnits="milliseconds"
          fields={getSubscriberGroupFields(subscriberGroup, metricLatencyTooltipMap[metric])}
          onInteractionData={setInteractionData}
          interactionData={interactionData}
          valuesMap={itemsLatencyMap}
          xScale={xScale}
          widthChart={widthChart}
          hours={hours}
          metric={metric}
          setMetric={setMetric}
          facet="latency"
          warning={warningEnabled ? thresholds[metricThresholdMap[metric]] : undefined}
          metricChoices={metricChoices}
        />
        <FacetGraph
          title="Packet retransmissions Over Time"
          items={itemsRetransmission.filter(isInsideRange(range))}
          yAxisUnits="% retransmissions"
          fields={getSubscriberGroupFields(subscriberGroup)}
          onInteractionData={setInteractionData}
          interactionData={interactionData}
          valuesMap={itemsRetransmissionMap}
          xScale={xScale}
          widthChart={widthChart}
          hours={hours}
          warning={warningEnabled ? thresholds["RTX"] : undefined}
        />
        <FacetGraph
          title="Congestion Over Time"
          items={itemsCongestion.filter(isInsideRange(range))}
          yAxisUnits="% congestion"
          fields={[{ name: "congestion", label: "Congestion-%" }]}
          onInteractionData={setInteractionData}
          interactionData={interactionData}
          valuesMap={itemsCongestionMap}
          xScale={xScale}
          widthChart={widthChart}
          hours={hours}
          warning={warningEnabled ? thresholds["CONGEST"] : undefined}
        />
        <FacetGraph
          facet="active"
          title="Active Subscribers Over Time"
          items={itemsActiveSubscribers.filter(isInsideRange(range))}
          yAxisUnits="subscribers"
          fields={[{ name: "activeSubscribers", label: "Subscribers" }]}
          onInteractionData={setInteractionData}
          interactionData={interactionData}
          valuesMap={itemsActiveSubscribersMap}
          xScale={xScale}
          widthChart={widthChart}
          hours={hours}
        />
        <TileContainer className="chart-container">
          <h4 className="chart-title lowercase">Congestion vs Average Speed</h4>
          <div className="graph">
            <ScatterPlot
              items={scatterItemsXSpeed(
                itemsCongestion,
                volumes,
                "congestion"
              ).filter(isInsideRange(range))}
              contentY={{
                yAxisLabel: "Congested traffic (%)",
                tooltipLabel: "Congestion",
                tooltipUnit: "%",
              }}
              contentX={{
                xAxisLabel: "Average downlink speed (Mbps)",
                tooltipLabel: "Average Speed",
                tooltipUnit: "Mbps",
              }}
              interactionData={interactionData}
              onInteractionData={setInteractionData}
              warning={warningEnabled ? thresholds["CONGEST"] : undefined}
            />
          </div>
        </TileContainer>
        <ScatterPlotLatencySpeed
          items={scatterItemsXSpeed(
            itemsLatency,
            volumes,
            subscriberGroup
          ).filter(isInsideRange(range))}
          interactionData={interactionData}
          onInteractionData={setInteractionData}
          ref={scatterPlotRef}
          metric={metric}
          setMetric={setMetric}
          warning={warningEnabled && metric === "Rtt-Min-Dn"? thresholds["RTT-ms"] : undefined}
        />
        <TileContainer className="chart-container">
          <h4 className="chart-title lowercase">
            Packet Retransmissions vs Average Speed
          </h4>
          <div className="graph">
            <ScatterPlot
              items={scatterItemsXSpeed(
                itemsRetransmission,
                volumes,
                subscriberGroup
              ).filter(isInsideRange(range))}
              contentY={{
                yAxisLabel: "Retransmissions (%)",
                tooltipLabel: "Retransmissions",
                tooltipUnit: "%",
              }}
              contentX={{
                xAxisLabel: "Average downlink speed (Mbps)",
                tooltipLabel: "Average Speed",
                tooltipUnit: "Mbps",
              }}
              interactionData={interactionData}
              onInteractionData={setInteractionData}
              warning={warningEnabled ? thresholds["RTX"] : undefined}
            />
          </div>
        </TileContainer>
      </ContextControls>
    </ColumnsGrid>
  );
};

const systemTimeRetrieval = () => doSystemDateRetrieval().then(parseSystemDate);

const promisesInfo = [
  { facet: "mbps", parse: parseSubscriberVolume },
  { facet: "latency-down", parse: parseSubscriberLatencies },
  { facet: "latency-percentile-med", parse: parseSubscriberLatencies },
  { facet: "latency-percentile-80p", parse: parseSubscriberLatencies },
  { facet: "flows", parse: parseAndCalcFlowsCreationPerMinute },
  { facet: "retransmission", parse: parseSubscriberRetransmissions },
  { facet: "congest", parse: parseSubscriberCongestion },
  { facet: "ip-active", parse: parseActiveSubscribers },
];

const metricLatencyTooltipMap = {
  "Rtt-80%-Dn":
    "The 80-th percentile of all downlink RTT latency, measured from BQN to end user, in 10 minutes.",
  "Rtt-Med-Dn":
    "The 50-th percentile (median) of all downlink RTT latency samples, measured from BQN to end user, in 5 minutes.",
  "Rtt-Min-Dn":
    "The mean of all downlink RTT minimum latency samples, measured in 1-second intervals, during active downloads, in 5 minutes.",
};

const doStatsRequests = ({
  subscriberGroup,
  target,
  hours,
  interval,
  mode,
  bqnId,
}) =>
  Promise.all(
    promisesInfo.map(({ facet, parse }) =>
      doStatsRetrieval({
        facet,
        target,
        hours,
        interval,
        subscriberGroup,
        mode,
        bqnId,
      })
        .then(parse)
        .catch((error) => console.error(error) || [])
    )
  );

const retrieval = ({
  subscriberGroup,
  target,
  hours = 24,
  interval,
  mode,
  bqnId,
}) => {
  return Promise.all([
    systemTimeRetrieval().then((systemDate) =>
      calcRangeExtent(systemDate, hours)
    ),
    doStatsRequests({ subscriberGroup, target, hours, interval, mode, bqnId }),
  ]).then(
    ([
      range,
      [
        volumes,
        itemsLatencyDown,
        itemsLatencyMed,
        itemsLatency80,
        itemsFlows,
        itemsRetransmission,
        itemsCongestion,
        itemsActiveSubscribers,
      ],
    ]) => ({
      range,
      volumes,
      itemsLatencyDown,
      itemsLatencyMed,
      itemsLatency80,
      itemsFlows,
      itemsRetransmission,
      itemsCongestion,
      itemsActiveSubscribers,
    })
  );
};

const _defaultContext = {
  hours: 24,
  interval: 10,
};

const intervalForHours = (hours, interval = 10) =>
  hours > 7 * 24 ? 60 : interval;

const SubscriberGroupDashboard = ({
  target,
  subscriberGroup,
  settingsSubscriberGroups,
}) => {
  const {
    bqnId,
    clusterStatus: { mode },
  } = useContext(BqnContext);
  const [request, setRequest] = useState(null);
  const [metric, setMetric] = useState("Rtt-Min-Dn");
  const [warningEnabled, setWarningEnabled] = useState(true);

  const initContext = {
    ..._defaultContext,
    hours: periodAsHours[settingsSubscriberGroups.timePeriod],
  };
  const [context, setContext] = useState(initContext);

  const setHoursInContext = (hours) =>
    setContext({ hours, interval: intervalForHours(hours) });
  const doLoad = () => {
    setRequest(retrieval({ subscriberGroup, target, mode, bqnId, ...context }));
  };

  const { hours, interval } = context;
  const targetStr = target ? (target.addr ? target.addr : target.subsId) : "";
  const contextStr = "" + hours + interval;

  useEffect(() => {
    doLoad();
  }, [targetStr, contextStr, bqnId]);

  return request === null ? null : (
    <Request during={request}>
      {(result) => (
        <Dashboard
          {...result}
          {...context}
          target={target}
          onHoursChange={setHoursInContext}
          subscriberGroup={subscriberGroup}
          settingsSubscriberGroups={{
            ...settingsSubscriberGroups,
            timePeriod: hoursToTimePeriod(hours),
          }}
          metric={metric}
          setMetric={setMetric}
          warningEnabled={warningEnabled}
          setWarningEnabled={setWarningEnabled}
        />
      )}
    </Request>
  );
};
export default SubscriberGroupDashboard;
